import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as api from './api';
import type {
  AttendanceBulkInput,
  AttendanceParams,
  GradebookDto,
  GradebookParams,
  GradeColumnListParams,
  GradeColumnWriteInput,
  ManualGradeUpsertInput,
} from './types';

/** Query-key factory — every progress key derives from here. */
export const progressKeys = {
  all: ['progress'] as const,
  gradebook: (params: GradebookParams) => [...progressKeys.all, 'gradebook', params] as const,
  gradebooks: () => [...progressKeys.all, 'gradebook'] as const,
  attendance: (params: AttendanceParams) => [...progressKeys.all, 'attendance', params] as const,
  attendances: () => [...progressKeys.all, 'attendance'] as const,
  pupil: (memberPublicId: string, schoolClass?: string) =>
    [...progressKeys.all, 'pupil', memberPublicId, schoolClass ?? null] as const,
  atRisk: (schoolClass: string) => [...progressKeys.all, 'at-risk', schoolClass] as const,
  columns: (params: GradeColumnListParams) =>
    [...progressKeys.all, 'grade-columns', params] as const,
  allColumns: () => [...progressKeys.all, 'grade-columns'] as const,
};

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export function useGradebook(params: GradebookParams, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: progressKeys.gradebook(params),
    queryFn: () => api.getGradebook(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? Boolean(params.school_class),
  });
}

export function useAttendanceRegister(
  params: AttendanceParams,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: progressKeys.attendance(params),
    queryFn: () => api.getAttendanceRegister(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? Boolean(params.school_class),
  });
}

export function usePupilProgress(memberPublicId: string, schoolClass?: string) {
  return useQuery({
    queryKey: progressKeys.pupil(memberPublicId, schoolClass),
    queryFn: () => api.getPupilProgress(memberPublicId, schoolClass),
    enabled: Boolean(memberPublicId),
  });
}

export function useAtRisk(schoolClass: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: progressKeys.atRisk(schoolClass),
    queryFn: () => api.getAtRisk(schoolClass),
    enabled: options.enabled ?? Boolean(schoolClass),
  });
}

export function useGradeColumns(
  params: GradeColumnListParams = {},
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: progressKeys.columns(params),
    queryFn: () => api.getGradeColumns(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

export function useCreateGradeColumn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GradeColumnWriteInput) => api.createGradeColumn(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: progressKeys.gradebooks() });
      void qc.invalidateQueries({ queryKey: progressKeys.allColumns() });
    },
  });
}

export function useUpdateGradeColumn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, input }: { publicId: string; input: GradeColumnWriteInput }) =>
      api.updateGradeColumn(publicId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: progressKeys.gradebooks() });
      void qc.invalidateQueries({ queryKey: progressKeys.allColumns() });
    },
  });
}

export function useArchiveGradeColumn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.archiveGradeColumn(publicId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: progressKeys.gradebooks() });
      void qc.invalidateQueries({ queryKey: progressKeys.allColumns() });
    },
  });
}

/**
 * Inline manual-grade edit: optimistic cell write against the live gradebook
 * payload, rolled back on failure.
 *
 * The optimistic cell deliberately carries `display: null` — the 12-point
 * rendering is the server's to compute (ADR-015), so the cell shows the raw
 * score the teacher typed until the invalidated read comes back with the
 * authoritative `display`.
 */
export function useUpsertManualGrade(params: GradebookParams) {
  const qc = useQueryClient();
  const key = progressKeys.gradebook(params);

  return useMutation({
    mutationFn: (input: ManualGradeUpsertInput) => api.upsertManualGrade(input),
    onMutate: async input => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<GradebookDto>(key);
      if (previous) {
        const score = input.score ?? null;
        qc.setQueryData<GradebookDto>(key, {
          ...previous,
          cells: {
            ...previous.cells,
            [input.pupil]: {
              ...previous.cells[input.pupil],
              [input.column]: {
                score,
                display: null,
                status: score === null ? 'pending' : 'graded',
              },
            },
          },
        });
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) qc.setQueryData(key, context.previous);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: key }),
  });
}

/** One bulk POST marks a whole lesson; the register read is then refetched. */
export function useBulkMarkAttendance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AttendanceBulkInput) => api.bulkMarkAttendance(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: progressKeys.attendances() }),
  });
}
